import React  from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, 
  Select, MenuItem, InputLabel, FormControl, Alert } from '@mui/material';
import { AdSpaceInterface, AdSpaceMediaTypeInterface, SizeDataTypeInterface, 
  CurrencyInterface } from '../../../interfaces/interfaces';

interface Props {
  open: boolean;
  adSpaceToUpdate: AdSpaceInterface | null;
  setAdSpaceToUpdate: React.Dispatch<React.SetStateAction<AdSpaceInterface | null>>;
  updateForm: Partial<AdSpaceInterface>;
  setUpdateForm: React.Dispatch<React.SetStateAction<Partial<AdSpaceInterface>>>;
  onClose: () => void;
  onSave: () => void;
  updateError?: string | null;
  updateSuccess?: string | null;
  mediaTypes: AdSpaceMediaTypeInterface[];
  sizeDataTypes: SizeDataTypeInterface[];
  currencies: CurrencyInterface[];
}
export const AdSpacesUpdateDialog: React.FC<Props> = ({
  open,
  adSpaceToUpdate,
  setAdSpaceToUpdate,
  updateForm,
  setUpdateForm,
  onClose,
  onSave,
  updateError,
  updateSuccess,
  mediaTypes,
  sizeDataTypes,
  currencies
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>Update Ad Space</DialogTitle>
    <DialogContent>
      {updateError && <Alert severity="error" sx={{ mb: 2 }}>{updateError}</Alert>}
      {updateSuccess && <Alert severity="success" sx={{ mb: 2 }}>{updateSuccess}</Alert>}
      <TextField
        label="Geo Name"
        value={updateForm.geoName ?? adSpaceToUpdate?.geoName ?? ''}
        onChange={e => setUpdateForm(f => ({ ...f, geoName: e.target.value }))}
        fullWidth
        margin="normal"
      />
      <TextField
        label="Description"
        value={updateForm.description ?? adSpaceToUpdate?.description ?? ''}
        onChange={e => setUpdateForm(f => ({ ...f, description: e.target.value }))}
        fullWidth
        margin="normal"
      />
      <TextField
        label="User ID"
        type="number"
        value={updateForm.userId ?? adSpaceToUpdate?.userId ?? ''}
        onChange={e => setUpdateForm(f => ({ ...f, userId: Number(e.target.value) }))}
        fullWidth
        margin="normal"
      />

      <FormControl fullWidth margin="normal">
        <InputLabel id="media-type-label">Ad Space Media Type</InputLabel>
        <Select
          labelId="media-type-label"
          value={updateForm.adSpaceMediaTypeId ?? adSpaceToUpdate?.adSpaceMediaTypeId ?? ''}
          label="Ad Space Media Type"
          onChange={e => setUpdateForm(f => ({ ...f, adSpaceMediaTypeId: Number(e.target.value) }))}
          required
        >
          {mediaTypes.map(mt => (
            <MenuItem key={mt.id} value={mt.id}>
              {mt.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label="Width"
        type="number"
        value={updateForm.sizeWidth ?? adSpaceToUpdate?.sizeWidth ?? ''}
        onChange={e => setUpdateForm(f => ({ ...f, sizeWidth: Number(e.target.value) }))}
        fullWidth
        margin="normal"
      />
      <TextField
        label="Height"
        type="number"
        value={updateForm.sizeHeight ?? adSpaceToUpdate?.sizeHeight ?? ''}
        onChange={e => setUpdateForm(f => ({ ...f, sizeHeight: Number(e.target.value) }))}
        fullWidth
        margin="normal"
      />

      <FormControl fullWidth margin="normal">
        <InputLabel id="size-data-type-label">Size Data Type</InputLabel>
        <Select
          labelId="size-data-type-label"
          value={updateForm.sizeDataTypeId ?? adSpaceToUpdate?.sizeDataTypeId ?? ''}
          label="Size Data Type"
          onChange={e => setUpdateForm(f => ({ ...f, sizeDataTypeId: Number(e.target.value) }))}
          required
        >
          {sizeDataTypes.map(sdt => (
            <MenuItem key={sdt.id} value={sdt.id}>
              {sdt.unitName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label="Country"
        value={updateForm.country ?? adSpaceToUpdate?.country ?? ''}
        onChange={e => setUpdateForm(f => ({ ...f, country: e.target.value }))}
        fullWidth
        margin="normal"
      />
      <TextField
        label="Province"
        value={updateForm.province ?? adSpaceToUpdate?.province ?? ''}
        onChange={e => setUpdateForm(f => ({ ...f, province: e.target.value }))}
        fullWidth
        margin="normal"
      />
      <TextField
        label="City"
        value={updateForm.city ?? adSpaceToUpdate?.city ?? ''}
        onChange={e => setUpdateForm(f => ({ ...f, city: e.target.value }))}
        fullWidth
        margin="normal"
      />
      <TextField
        label="Area"
        value={updateForm.area ?? adSpaceToUpdate?.area ?? ''}
        onChange={e => setUpdateForm(f => ({ ...f, area: e.target.value }))}
        fullWidth
        margin="normal"
      />
      <TextField
        label="Street"
        value={updateForm.street ?? adSpaceToUpdate?.street ?? ''}
        onChange={e => setUpdateForm(f => ({ ...f, street: e.target.value }))}
        fullWidth
        margin="normal"
      />
      {/* For longLatCoords, you may want a custom input or just show as JSON for now */}
      <TextField
        label="LongLat Coords (JSON)"
        value={
          updateForm.longLatCoords
            ? JSON.stringify(updateForm.longLatCoords)
            : adSpaceToUpdate?.longLatCoords
              ? JSON.stringify(adSpaceToUpdate.longLatCoords)
              : ''
        }
        onChange={e => {
          try {
            setUpdateForm(f => ({
              ...f,
              longLatCoords: JSON.parse(e.target.value)
            }));
          } catch {
            // ignore parse error for now
          }
        }}
        fullWidth
        margin="normal"
      />
      <TextField
        label="Price Weekly"
        type="number"
        value={updateForm.priceWeekly ?? adSpaceToUpdate?.priceWeekly ?? ''}
        onChange={e => setUpdateForm(f => ({ ...f, priceWeekly: Number(e.target.value) }))}
        fullWidth
        margin="normal"
      />
      <TextField
        label="Price Monthly"
        type="number"
        value={updateForm.priceMonthly ?? adSpaceToUpdate?.priceMonthly ?? ''}
        onChange={e => setUpdateForm(f => ({ ...f, priceMonthly: Number(e.target.value) }))}
        fullWidth
        margin="normal"
      />
      <TextField
        label="Price Yearly"
        type="number"
        value={updateForm.priceYearly ?? adSpaceToUpdate?.priceYearly ?? ''}
        onChange={e => setUpdateForm(f => ({ ...f, priceYearly: Number(e.target.value) }))}
        fullWidth
        margin="normal"
      />

      <FormControl fullWidth margin="normal">
        <InputLabel id="currency-label">Currency</InputLabel>
        <Select
          labelId="currency-label"
          value={updateForm.currencyId ?? adSpaceToUpdate?.currencyId ?? ''}
          label="Currency"
          onChange={e => setUpdateForm(f => ({ ...f, currencyId: Number(e.target.value) }))}
          required
        >
          {currencies.map(currency => (
            <MenuItem key={currency.id} value={currency.id}>
              {currency.code} - {currency.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label="Available From"
        type="datetime-local"
        value={
          updateForm.availableFrom
            ? new Date(updateForm.availableFrom).toISOString().slice(0, 16)
            : adSpaceToUpdate?.availableFrom
              ? new Date(adSpaceToUpdate.availableFrom).toISOString().slice(0, 16)
              : ''
        }
        onChange={e => setUpdateForm(f => ({ ...f, availableFrom: new Date(e.target.value) }))}
        fullWidth
        margin="normal"
        InputLabelProps={{ shrink: true }}
      />
      <FormControl fullWidth margin="normal">
        <InputLabel id="active-label">Active</InputLabel>
        <Select
          labelId="active-label"
          value={
            updateForm.active !== undefined
              ? updateForm.active
                ? 'yes'
                : 'no'
              : adSpaceToUpdate?.active
                ? 'yes'
                : 'no'
          }
          label="Active"
          onChange={e =>
            setUpdateForm(f => ({
              ...f,
              active: e.target.value === 'yes'
            }))
          }
        >
          <MenuItem value="yes">Yes</MenuItem>
          <MenuItem value="no">No</MenuItem>
        </Select>
      </FormControl>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button color="primary" onClick={onSave}>Update</Button>
    </DialogActions>
  </Dialog>
);